// The workflow runtime engine (Phase 0, ENG-169).
//
// ONE implementation, THREE deployments: it runs in-process in the web
// players, inside the v1 runtime edge function, and is mirrored in Swift for
// iOS offline play. Long-term the API is the exit ramp from maintaining the
// Swift copy at all — so everything here stays pure and free of I/O.
//
// Three responsibilities:
//   1. `buildFacts`    — the ONE session fact-map supplier.
//   2. `applyActions`  — the pure session-variable executor.
//   3. `advance`       — one step through the graph, in the normative order.
//
// NORMATIVE ORDERING (the iOS contract — deviating from this desynchronises
// the platforms):
//
//   answer commit → attempts+1 → correct computed → routing evaluated against
//   PRE-action facts → traversed edge's actions → target entered → visits+1 →
//   on_enter actions → render against live facts
//
// "Pre-action" is specifically about the edge being traversed: the route is
// chosen before that edge's actions fire, so an action can never influence the
// decision that selected it.
import { CARD_PORT_NEXT, CONDITIONAL_PORT_OTHERWISE, conditionalRulePort, } from '@little-loop/shared-types';
import { evaluateRule } from './evaluateRule.js';
/**
 * Upper bound on node hops in a single `advance` call.
 *
 * Cards may legally loop back on each other (BUS-18), so a cycle is not an
 * error — but a cycle with no card in it would spin forever. Conditionals are
 * pure routing and are traversed without pausing, so this only bounds
 * *consecutive* routing hops, never the length of a play.
 */
export const MAX_ROUTING_HOPS = 100;
// ── 1. The fact-map supplier ────────────────────────────────────────────────
/**
 * Build the flat `Record<string,string>` the frozen rule evaluator consumes.
 *
 * Session built-ins are emitted under canonical `session.*` keys; author
 * variables under `session.<name>`. `externalFacts` supplies the already-scoped
 * `recipient.*` / `user.*` / `manager.*` / `org.*` entries — this function never
 * invents them, so there is exactly one place each scope is produced.
 *
 * Everything is stringified because the rule language is stringly typed and
 * `evaluateRule` does its own numeric coercion.
 */
export function buildFacts(state, externalFacts = {}) {
    const facts = { ...externalFacts };
    for (const [key, value] of Object.entries(state.answers)) {
        facts[`session.answers.${key}`] = value;
    }
    for (const [key, value] of Object.entries(state.correct)) {
        facts[`session.correct.${key}`] = value ? 'true' : 'false';
    }
    for (const [nodeId, count] of Object.entries(state.visits)) {
        facts[`session.visits.${nodeId}`] = String(count);
    }
    for (const [key, count] of Object.entries(state.attempts)) {
        facts[`session.attempts.${key}`] = String(count);
    }
    facts['session.restarts'] = String(state.restarts);
    for (const [name, value] of Object.entries(state.variables)) {
        facts[`session.${name}`] = stringifyValue(value);
    }
    return facts;
}
function stringifyValue(value) {
    return typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value);
}
// ── 2. The action executor ──────────────────────────────────────────────────
/**
 * Apply session actions, returning a NEW variables map. Pure — the input is
 * never mutated.
 *
 * Actions targeting an undeclared variable are IGNORED rather than throwing:
 * publish-time validation (`validateWorkflowGraph`) is what rejects them, and a
 * runtime that hard-failed here would let a bad publish break an in-flight
 * play. `onIgnored` surfaces them for the Simulate drawer and API traces.
 */
export function applyActions(variables, actions, declared, onIgnored) {
    if (actions.length === 0)
        return variables;
    const byName = new Map(declared.map((def) => [def.name, def]));
    const next = { ...variables };
    for (const action of actions) {
        const def = byName.get(action.target);
        if (!def) {
            onIgnored?.(action, `"${action.target}" is not a declared session variable`);
            continue;
        }
        switch (action.op) {
            case 'set':
                next[action.target] = coerce(action.value ?? def.initial_value, def);
                break;
            case 'increment':
            case 'decrement': {
                if (def.type !== 'number') {
                    onIgnored?.(action, `${action.op} requires a number variable; "${def.name}" is ${def.type}`);
                    continue;
                }
                const delta = toNumber(action.value ?? 1);
                if (delta === null) {
                    onIgnored?.(action, `${action.op} value "${String(action.value)}" is not numeric`);
                    continue;
                }
                const current = toNumber(next[action.target] ?? 0) ?? 0;
                next[action.target] = action.op === 'increment' ? current + delta : current - delta;
                break;
            }
        }
    }
    return next;
}
/** Coerce an action value to the variable's declared type. */
function coerce(value, def) {
    switch (def.type) {
        case 'number':
            return toNumber(value) ?? 0;
        case 'boolean':
            return typeof value === 'boolean' ? value : value === 'true' || value === 1;
        case 'string':
            return stringifyValue(value);
    }
}
function toNumber(value) {
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : null;
    if (typeof value === 'boolean')
        return value ? 1 : 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}
// ── Session lifecycle ───────────────────────────────────────────────────────
/** Initial state for a fresh play. The start node is NOT yet entered. */
export function startSession(workflowVersionId, declared = []) {
    const variables = {};
    for (const def of declared) {
        variables[def.name] = coerce(def.initial_value, def);
    }
    return {
        workflow_version_id: workflowVersionId,
        current_node_id: null,
        answers: {},
        correct: {},
        visits: {},
        attempts: {},
        restarts: 0,
        variables,
        outcome_code: null,
    };
}
/**
 * Restart semantics (design doc, BUS-15).
 *
 * Clears answers and correctness; **KEEPS visits and attempts** — those are the
 * loop-guard rails and a restart that reset them would let a learner cycle a
 * two-strikes gate forever. Increments `restarts`. Author variables return to
 * their declared initial values.
 *
 * This never touches assignment rows: the BUS-15 guardian reset (which creates
 * a NEW assignment row) is a separate, unchanged concept.
 */
export function restartSession(state, declared = []) {
    const variables = {};
    for (const def of declared) {
        variables[def.name] = coerce(def.initial_value, def);
    }
    return {
        ...state,
        current_node_id: null,
        answers: {},
        correct: {},
        visits: { ...state.visits },
        attempts: { ...state.attempts },
        restarts: state.restarts + 1,
        variables,
        outcome_code: null,
    };
}
/**
 * Take one step through the graph.
 *
 * On a fresh state (`current_node_id === null`) this ENTERS the start node
 * without consuming an answer. Otherwise it commits `answer`, routes out of the
 * current node, and walks through any conditional nodes until it reaches a card
 * or an outcome.
 */
export function advance(graph, state, answer, options = {}) {
    const declared = options.declared ?? [];
    const externalFacts = options.externalFacts ?? {};
    const trace = [];
    const ignoredActions = [];
    const noteIgnored = (action, reason) => {
        ignoredActions.push({ action, reason });
    };
    const nodes = new Map(graph.nodes.map((node) => [node.id, node]));
    let working = { ...state };
    // Entering the graph for the first time: no answer to commit, no edge to
    // traverse — enter the start node directly.
    if (working.current_node_id === null && working.outcome_code === null) {
        const start = nodes.get(graph.start_node_id);
        if (!start) {
            return fail(working, trace, ignoredActions, `start node "${graph.start_node_id}" not found`);
        }
        return enterAndSettle(graph, nodes, working, start, trace, ignoredActions, {
            declared,
            externalFacts,
            noteIgnored,
            fromNodeId: null,
            edgeId: null,
            reason: 'start',
        });
    }
    if (working.outcome_code !== null) {
        return fail(working, trace, ignoredActions, 'session has already reached an outcome');
    }
    const current = working.current_node_id === null ? undefined : nodes.get(working.current_node_id);
    if (!current) {
        return fail(working, trace, ignoredActions, `current node "${working.current_node_id}" not found`);
    }
    // ── answer commit → attempts+1 → correct computed ──────────────────────────
    if (answer) {
        working = {
            ...working,
            answers: { ...working.answers, [answer.answer_key]: answer.value },
            attempts: {
                ...working.attempts,
                [answer.answer_key]: (working.attempts[answer.answer_key] ?? 0) + 1,
            },
            correct: answer.correct === undefined
                ? working.correct
                : { ...working.correct, [answer.answer_key]: answer.correct },
        };
    }
    // ── routing evaluated against PRE-action facts ─────────────────────────────
    const preActionFacts = buildFacts(working, externalFacts);
    const exit = selectExit(current, preActionFacts, graph.edges);
    if (!exit) {
        return fail(working, trace, ignoredActions, `node "${current.id}" has no outgoing edge for port "${portFor(current, preActionFacts)}"`);
    }
    const target = nodes.get(exit.edge.target);
    if (!target) {
        return fail(working, trace, ignoredActions, `edge "${exit.edge.id}" targets missing node "${exit.edge.target}"`);
    }
    // ── traversed edge's actions ───────────────────────────────────────────────
    working = {
        ...working,
        variables: applyActions(working.variables, exit.edge.actions, declared, noteIgnored),
    };
    return enterAndSettle(graph, nodes, working, target, trace, ignoredActions, {
        declared,
        externalFacts,
        noteIgnored,
        fromNodeId: current.id,
        edgeId: exit.edge.id,
        reason: exit.reason,
    });
}
/**
 * Enter `node`, then keep walking while it is pure routing (a conditional),
 * stopping at the first card or outcome.
 */
function enterAndSettle(graph, nodes, state, node, trace, ignoredActions, ctx) {
    let working = state;
    let currentNode = node;
    let fromNodeId = ctx.fromNodeId;
    let edgeId = ctx.edgeId;
    let reason = ctx.reason;
    for (let hop = 0; hop < MAX_ROUTING_HOPS; hop++) {
        // ── target entered → visits+1 → on_enter actions ────────────────────────
        working = {
            ...working,
            current_node_id: currentNode.id,
            visits: { ...working.visits, [currentNode.id]: (working.visits[currentNode.id] ?? 0) + 1 },
        };
        trace.push({ from_node_id: fromNodeId, edge_id: edgeId, to_node_id: currentNode.id, reason });
        if (currentNode.type === 'card') {
            working = {
                ...working,
                variables: applyActions(working.variables, currentNode.on_enter, ctx.declared, ctx.noteIgnored),
            };
            return {
                status: 'card',
                state: working,
                card: currentNode,
                outcome: null,
                trace,
                error: null,
                ignoredActions,
            };
        }
        if (currentNode.type === 'outcome') {
            working = { ...working, outcome_code: currentNode.outcome_code, current_node_id: null };
            return {
                status: 'outcome',
                state: working,
                card: null,
                outcome: currentNode,
                trace,
                error: null,
                ignoredActions,
            };
        }
        if (currentNode.type === 'variant_fork') {
            // Forks are resolved and PRUNED server-side before play. Seeing one at
            // runtime means an unresolved graph was served — fail loudly.
            return fail(working, trace, ignoredActions, `unresolved variant_fork node "${currentNode.id}" reached at runtime; the graph was not resolved for a recipient`);
        }
        // Conditional: pure routing, so keep walking without pausing to render.
        const facts = buildFacts(working, ctx.externalFacts);
        const exit = selectExit(currentNode, facts, graph.edges);
        if (!exit) {
            return fail(working, trace, ignoredActions, `conditional "${currentNode.id}" has no outgoing edge for port "${portFor(currentNode, facts)}"`);
        }
        const next = nodes.get(exit.edge.target);
        if (!next) {
            return fail(working, trace, ignoredActions, `edge "${exit.edge.id}" targets missing node "${exit.edge.target}"`);
        }
        working = {
            ...working,
            variables: applyActions(working.variables, exit.edge.actions, ctx.declared, ctx.noteIgnored),
        };
        fromNodeId = currentNode.id;
        edgeId = exit.edge.id;
        reason = exit.reason;
        currentNode = next;
    }
    return fail(working, trace, ignoredActions, `routing exceeded ${MAX_ROUTING_HOPS} consecutive hops without reaching a card or outcome`);
}
/** Which port a node leaves through, given the current facts. */
function portFor(node, facts) {
    if (node.type === 'card')
        return CARD_PORT_NEXT;
    if (node.type === 'conditional') {
        const index = node.rules.findIndex((rule) => evaluateRule(rule.rule, facts));
        return index === -1 ? CONDITIONAL_PORT_OTHERWISE : conditionalRulePort(index);
    }
    return '';
}
/** Resolve the node's active port to its single outgoing edge. */
function selectExit(node, facts, edges) {
    if (node.type === 'outcome' || node.type === 'variant_fork')
        return null;
    const port = portFor(node, facts);
    const edge = edges.find((e) => e.source === node.id && e.source_port === port);
    if (!edge)
        return null;
    let reason = port;
    if (node.type === 'conditional' && port !== CONDITIONAL_PORT_OTHERWISE) {
        const index = node.rules.findIndex((_, i) => conditionalRulePort(i) === port);
        const rule = index === -1 ? undefined : node.rules[index];
        if (rule)
            reason = rule.label ?? rule.id;
    }
    return { edge, reason };
}
function fail(state, trace, ignoredActions, error) {
    return { status: 'error', state, card: null, outcome: null, trace, error, ignoredActions };
}
